import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import {
  Box,
  capitalize,
  Card,
  CardHeader,
  Divider,
  Link as MuiLink,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { DateTime } from 'luxon'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import CopyToClipboardButton from '@/components/inputs/CopyToClipboardButton'
import CustomIconButton from '@/components/inputs/CustomIconButton'
import CustomCardContent from '@/components/layouts/CustomCardContent'
import ControlTypeChip from '@/features/resource/components/ControlTypeChip'
import EditResourceModal from '@/features/resource/components/EditResourceModal'
import ResourceEstimatedPower from '@/features/resource/components/ResourceEstimatedPower'
import ResourceSteeringTarget from '@/features/resource/components/ResourceSteeringTarget'
import { RESOURCE_TYPES } from '@/features/resource/constants'
import { useResourceData } from '@/features/resource/contexts/ResourceDataContext'
import type { Resource } from '@/features/resource/types'
import { SiteDetailsRouteInformation } from '@/features/site/pages/constants'
import { convertEnergyPower } from '@/utils/powerEnergyTransformations'

type ResourceCardProps = {
  content?: React.ReactNode
  showGeneralInfo?: boolean
}

type EditDialogState = {
  resource: Resource | null
  open: boolean
}

type ResourceTitlePanelProps = {
  resource: Resource
  /** Called when the user opens the edit dialog. Polling is suspended while editing. */
  onEditDialogOpen: () => void
  /** Called when the dialog closes (cancel, save, error — every path). Polling resumes. */
  onEditDialogClose: () => void
}

/**
 * Delay before polling resumes after a successful rename. Must outlast the BFF read replica's
 * Pulsar-sync lag from msa-resource-management — re-enabling the polling query causes an
 * immediate refetch (TanStack's default `staleTime: 0` treats cached data as always stale), and
 * if that refetch lands before the replica catches up it overwrites the optimistic
 * `setQueryData` with the OLD name. Polling's own ~5 s interval then catches the new name —
 * visible to the user as old → new → old → new flicker. 3 s is comfortably above the observed
 * replica lag (<1 s typically) without leaving polling off for long.
 */
const POLLING_RESUME_AFTER_SAVE_MS = 3000

export const ResourceTitlePanel = ({ resource, onEditDialogOpen, onEditDialogClose }: ResourceTitlePanelProps) => {
  const { t } = useTranslation()
  const [editDialog, setEditDialog] = useState<EditDialogState>({ open: false, resource: null })
  // When true, polling will resume after POLLING_RESUME_AFTER_SAVE_MS rather than immediately.
  // The save path sets this; the open/cancel paths clear it.
  const [deferredPollingResume, setDeferredPollingResume] = useState(false)

  // Polling lifecycle is anchored to the dialog open/close — not the mutation lifecycle. Opening
  // the dialog stops polling; cancelling restarts immediately; saving defers the restart so the
  // re-enable refetch doesn't race the Pulsar replica catch-up and overwrite the optimistic
  // setQueryData with stale data (see POLLING_RESUME_AFTER_SAVE_MS).
  function closeDialog() {
    setEditDialog({ resource: null, open: false })
  }

  function handleCloseDialog() {
    closeDialog()
    setDeferredPollingResume(false)
    onEditDialogClose()
  }

  function handleEditResourceSuccess() {
    closeDialog()
    setDeferredPollingResume(true)
  }

  function handleOpenDialog(resource: Resource) {
    setEditDialog({ resource, open: true })
    // If the user re-opens the dialog before the deferred restart fires, cancel it — polling
    // should stay off while a new edit is in progress. The useEffect cleanup clears the timer.
    setDeferredPollingResume(false)
    onEditDialogOpen()
  }

  // Capture the latest onEditDialogClose in a ref so the deferred-restart effect below depends
  // only on `deferredPollingResume` and isn't restarted (timer cleared + rescheduled from 0) on
  // every parent render that hands us a fresh function reference.
  const onEditDialogCloseRef = useRef(onEditDialogClose)
  useEffect(() => {
    onEditDialogCloseRef.current = onEditDialogClose
  }, [onEditDialogClose])

  useEffect(() => {
    if (!deferredPollingResume) return
    const timer = setTimeout(() => {
      onEditDialogCloseRef.current()
      setDeferredPollingResume(false)
    }, POLLING_RESUME_AFTER_SAVE_MS)
    return () => clearTimeout(timer)
  }, [deferredPollingResume])

  return (
    <CardHeader
      action={
        <>
          <CustomIconButton
            Icon={EditOutlinedIcon}
            aria-label={t('resources.table.edit_button_label')}
            color={'primary'}
            onClick={() => handleOpenDialog(resource)}
          />
          <EditResourceModal
            open={editDialog.open && editDialog.resource?.resourceID !== undefined}
            // We can assume that resource will always have an id, otherwise we should not show
            // the modal
            resourceID={editDialog.resource?.resourceID ?? ''}
            resourceName={editDialog.resource?.resourceName}
            resourceType={editDialog.resource?.resourceType}
            resourceTypeOptions={RESOURCE_TYPES}
            onClose={handleCloseDialog}
            onSuccess={handleEditResourceSuccess}
          />
        </>
      }
      subheader={resource.resourceName ?? t('common.unknown')}
      sx={{ paddingX: 0 }}
      title={
        <Stack alignItems="center" direction="row" gap={1}>
          {/*
           * The title is the site: site, customer and resource owner are one entity under three
           * names, and the resource's customerId is the siteUuid. It was already on the page, just
           * not linked — which is why there is no Site row in General information restating it.
           */}
          {resource.customerId === null ? (
            <span>{resource.customerName ?? t('common.unknown')}</span>
          ) : (
            <MuiLink component={Link} to={SiteDetailsRouteInformation.INDEX.navigationPathFor(resource.customerId)}>
              {resource.customerName ?? t('common.unknown')}
            </MuiLink>
          )}
          {resource.controlMode && <ControlTypeChip controlMode={resource.controlMode} />}
        </Stack>
      }
    ></CardHeader>
  )
}

const StyledResourceDetailRow = styled(TableRow)(() => ({
  '.MuiTableCell-root': {
    border: 'none',
  },
}))

const StyledResourceDetailHeaderColumn = styled(TableCell)(() => ({
  '.MuiTableCell-root': {
    border: 'none',
  },
}))

const ResourceDetailTable = ({ resource }: { resource: Resource }) => {
  const { t } = useTranslation()
  const rows = useMemo(() => {
    return [
      {
        label: t('resources.detail_panel.uuid'),
        // `component="span"` is load-bearing: MUI maps `variant="inherit"` to a <p>, so Stack's default
        // <div> would be invalid nesting. This app is client-rendered only, so nothing auto-closes the
        // paragraph — the cost is invalid markup plus a React validateDOMNesting warning, which the
        // spec pins. <span> and <button> are both valid phrasing content inside a <p>. The UUID is
        // wrapped too, because Stack's `spacing` (unlike `gap`) is sibling-selector based and needs two
        // *element* children; against a bare text node the gap is silently never applied.
        value: (
          <Stack alignItems="center" component="span" direction="row" spacing={0.5}>
            <span>{resource.resourceID}</span>
            <CopyToClipboardButton actionLabel={t('resources.detail_panel.copy_uuid')} value={resource.resourceID} />
          </Stack>
        ),
        column: 1,
        position: 1,
      },
      {
        label: t('resources.detail_panel.markets'),
        value: resource.marketPrograms.map((marketProgram) => t(`common.market_program.${marketProgram}`)).join(', '),
        column: 1,
        position: 2,
      },
      {
        label: t('resources.detail_panel.verified_capacity'),
        value: resource.verifiedCapacity
          ? convertEnergyPower(resource.verifiedCapacity, 'kilowatts')
          : t('common.not_set'),
        column: 1,
        position: 3,
      },
      {
        label: t('resources.detail_panel.estimated_power'),
        value: <ResourceEstimatedPower resource={resource} typographyVariant={'inherit'} />,
        column: 1,
        position: 4,
      },
      {
        label: t('resources.detail_panel.lifecycle_stage'),
        value:
          resource.lifecycleStage !== null ? capitalize(resource.lifecycleStage.toLowerCase()) : t('common.not_set'),
        column: 1,
        position: 5,
      },
      {
        label: t('resources.detail_panel.resource_type'),
        value: resource.resourceType !== null ? capitalize(resource.resourceType.toLowerCase()) : t('common.not_set'),
        column: 1,
        position: 6,
      },
      {
        label: t('resources.detail_panel.steerability_status'),
        value:
          (resource.steerabilityStatus ?? resource.status)
            ? capitalize((resource.steerabilityStatus ?? resource.status)!.toLowerCase())
            : t('common.not_set'),
        column: 2,
        position: 1,
      },
      {
        label: t('resources.detail_panel.steering_status'),
        value: resource.steeringStatus ? capitalize(resource.steeringStatus.toLowerCase()) : t('common.not_set'),
        column: 2,
        position: 2,
      },
      {
        label: t('resources.detail_panel.steering_target'),
        value: <ResourceSteeringTarget currentSteeringTarget={resource.steeringData?.currentSteeringTarget} />,
        column: 2,
        position: 3,
      },
      {
        label: t('resources.detail_panel.resource_can_consume'),
        value:
          resource.canConsume !== undefined && resource.canConsume !== null
            ? capitalize(resource.canConsume.toString())
            : t('common.not_set'),
        column: 2,
        position: 4,
      },
      {
        label: t('resources.detail_panel.resource_can_produce'),
        value:
          resource.canProduce !== undefined && resource.canProduce !== null
            ? capitalize(resource.canProduce.toString())
            : t('common.not_set'),
        column: 2,
        position: 5,
      },
      {
        label: t('resources.detail_panel.resource_last_update'),
        value: resource.lastUpdate ? DateTime.fromISO(resource.lastUpdate).toFormat('dd MMMM, HH:mm') : '-',
        column: 2,
        position: 6,
      },
    ]
  }, [resource, t])

  // Determine the number of columns by finding the maximum column number in the rows
  const columns = Math.max(...rows.map((row) => row.column))

  // Group rows by their column number and sort them by their position within the column
  const columnsData = Array.from({ length: columns }, (_, i) =>
    rows.filter((row) => row.column === i + 1).sort((a, b) => a.position - b.position),
  )

  return (
    <>
      <CardHeader sx={{ paddingX: 0 }} title={t('resources.detail_panel.general_information')}></CardHeader>
      <Box display="flex">
        {columnsData.map((columnRows) => (
          <Table key={columnRows[0].column} aria-label={t('resources.detail_panel.general_information')} size="small">
            <TableBody>
              {columnRows.map((row) => (
                <StyledResourceDetailRow key={row.label}>
                  <StyledResourceDetailHeaderColumn>
                    <CustomTypography color="grey.500" variant="caption">
                      {row.label}
                    </CustomTypography>
                  </StyledResourceDetailHeaderColumn>
                  <TableCell>
                    <CustomTypography sx={{ lineHeight: 'inherit' }} variant="inherit">
                      {row.value}
                    </CustomTypography>
                  </TableCell>
                </StyledResourceDetailRow>
              ))}
            </TableBody>
          </Table>
        ))}
      </Box>
    </>
  )
}

const ResourceCard = ({ content, showGeneralInfo = true }: ResourceCardProps) => {
  const { resource, stopPolling, restartPolling } = useResourceData()

  if (resource === undefined || resource === null) {
    return null
  }

  return (
    // `overflow: visible` overrides MUI's Card default (`overflow: hidden`), which would otherwise
    // become the containing block for any `position: sticky` descendant — breaking the sticky chart
    // sidebar on the Resource configuration tab. Pages with content that needs corner-clipping
    // would have to opt back in via their own wrapper; none currently do.
    <Card sx={{ marginY: 3, overflow: 'visible' }}>
      <CustomCardContent>
        <Stack sx={{ flexDirection: 'column', gap: 2 }}>
          <ResourceTitlePanel
            resource={resource}
            // Polling is suspended while the edit dialog is open and resumed when it closes,
            // regardless of outcome (cancel / save success / save error).
            onEditDialogClose={restartPolling}
            onEditDialogOpen={stopPolling}
          />
          <Divider />
          {showGeneralInfo && (
            <>
              <ResourceDetailTable resource={resource} />
              {content && <Divider />}
            </>
          )}
          {content}
        </Stack>
      </CustomCardContent>
    </Card>
  )
}

export default ResourceCard
